import React, { useEffect, useState} from "react";
import { Helmet } from "react-helmet";
import {
  Button as MuiButton,
  Card,
  CardContent,
  Chip,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { RecordsPage, Title, SubTitle, NavLink, Hr } from "./style";
import Container from "../../components/ui/ui kit/Container";
import Button from "../../components/ui/ui kit/Button";
import cl from "./Records.module.sass";
import { useAuth } from "../../hooks/useAuth";
import { CreateLeiService } from "../../api/services/create-lei";
import { ILei } from "../../types/lei";
import Loader from "../../components/ui/Loader";
import { muiTheme } from "../../theme";

const formatDate = (date: string) => date ? date.slice(0, 19).replace("T", " ") : "";

export const Records = () => {
  const [isCreationsShow, setCreationsShow] = useState<boolean>(false);
  const { isAuth } = useAuth();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const [records, setRecords] = useState<ILei[]>([]);
  const [loading, setLoading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuLei, setMenuLei] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const fetchRecords = async () => {
      try {
        setLoading(true);
        const res = await CreateLeiService.requestLeiList();
        const lei = res.data.map((el: any) => ({
          lei: el.lei_number,
          status: el.registration_status,
          date: el.next_renewal_date,
          name: el.company_name,
          country: el.legal_jurisdiction,
        }));
        setRecords(lei);
      } catch (error) {
        console.log('--> Error in getting records:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecords();
  }, []);

  const renderActions = (lei: string) => (
    <>
      <MuiButton
        size="small"
        variant="outlined"
        onClick={(e) => {
          setMenuAnchor(e.currentTarget);
          setMenuLei(lei);
        }}
      >
        Actions
      </MuiButton>
      <Menu
        anchorEl={menuAnchor}
        open={menuLei === lei}
        onClose={() => setMenuLei(null)}
      >
        {/* Operations on this LEI (e.g. request an update, download a
        certificate) will be added here. */}
      </Menu>
    </>
  );

  return (
    <Container>
      <Helmet> {/* Add the Helmet component */}
        <title>Records of LEI codes at LEIpapa</title>
        <meta
          name="description"
          content="Below are the records you have either registered, or renewed.
          The records (if any) are presented in order by the records approaching their Next Renewal Date."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {isAuth && (
        <RecordsPage>
          <div className={cl["title_wrap"]}>
            <Title>Records</Title>
          </div>
          <SubTitle>
              Press "Create LEI Application"👇 to Register, Renew or Transfer an LEI.
          </SubTitle>
          <div className={cl["create_lei-wrap"]}>
            <div className={cl["inner_button-lei"]}>
              <button
                className={cl["button"]}
                onClick={() => setCreationsShow((prev) => !prev)}
              >
                Create LEI Application
              </button>
            </div>
            {isCreationsShow && (
              <div className={cl["create_leicode"]}>
                <div className={cl["hidden_top"]}>
                  <div className={cl["wrap_button"]}>
                    <Button styleType="primary" component={NavLink} to="/create" width="auto" height="34px" noMarginTop>
                      Create new LEI
                    </Button>
                  </div>
                  <p className={cl["top_text"]}>
                    Submit your request using a standard web template. This
                    option is useful for single or low volume submissions
                  </p>
                </div>

                <Hr />

                <div className={cl["hidden_bottom"]}>
                  <div className={cl["wrap_button"]}>
                    <Button styleType="primary" component={NavLink} to="/renew" width="auto" height="34px" noMarginTop>
                      Renew LEI
                    </Button>
                  </div>
                  <p className={cl["bottom_text"]}>
                    Renew here any existing LEI. To keep an LEI active, it must be renewed every year. We use a fast, automated LEI renewal process.
                  </p>
                </div>

                <Hr />

                <div className={cl["hidden_bottom"]}>
                  <div className={cl["wrap_button"]}>
                    <Button styleType="primary" component={NavLink} to="/transfer" width="auto" height="34px" noMarginTop>
                      Transfer LEI
                    </Button>
                  </div>
                  <p className={cl["bottom_text"]}>
                    Transfer your LEI to LEIpapa for full service. If another service provider manages your LEI, you can transfer it to LEIpapa for free.
                  </p>
                </div>

              </div>
            )}
            <SubTitle>
              Below are the records you have either registered, or renewed. <br />The
              records (if any) are presented in order by the records approaching their
              Next Renewal Date.
            </SubTitle>

            <Hr />

            <div className={cl["table_wrap"]}>
              {loading ? (
                <Loader />
              ) : records.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                  No data found
                </Typography>
              ) : isMobile ? (
                <Stack spacing={1.5}>
                  {records.map(({ lei, name, country, status, date }, index) => (
                    <Card key={lei ?? index} variant="outlined">
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {name}
                          </Typography>
                          <Chip size="small" label={status || "n/a"} color={status === "ISSUED" ? "success" : "error"} />
                        </Stack>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <NavLink to={`/lei-details/${lei}`} target="_blank" rel="noopener noreferrer" style={{ color: "#4B7196", fontWeight: 500 }}>
                            {lei}
                          </NavLink>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {country} {date && `· Next renewal: ${formatDate(date)}`}
                        </Typography>
                        <Stack direction="row" sx={{ mt: 1.5 }}>
                          {renderActions(lei)}
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>LEI</TableCell>
                        <TableCell>Legal Name</TableCell>
                        <TableCell align="center">Country</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell align="center">Next renewal date</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {records.map(({ lei, name, country, status, date }, index) => (
                        <TableRow key={lei ?? index} hover>
                          <TableCell>
                            <NavLink to={`/lei-details/${lei}`} target="_blank" rel="noopener noreferrer" style={{ color: "#4B7196", fontWeight: 500 }}>
                              {lei}
                            </NavLink>
                          </TableCell>
                          <TableCell>{name}</TableCell>
                          <TableCell align="center">{country}</TableCell>
                          <TableCell align="center">
                            <Chip size="small" label={status || "n/a"} color={status === "ISSUED" ? "success" : "error"} />
                          </TableCell>
                          <TableCell align="center">{formatDate(date)}</TableCell>
                          <TableCell align="right">{renderActions(lei)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </div>
          </div>
        </RecordsPage>
      )}
    </Container>
  );
};
